/**
 * Yandex Metrika: the counter's own script, loaded from Yandex when a page starts it.
 *
 * Its usual snippet is a script written into the page, which the content policy does not
 * allow: this module does what the snippet does (a queue for the calls made before the
 * script arrives), and the gateway allows Yandex's addresses (docs/seo.md). `defer`: the
 * counter sends no visit by itself, the application says which page is shown, as it changes.
 */
import type { AnalyticsConfig } from './config';
import type { AnalyticsProvider } from './provider';

export const METRIKA_SCRIPT = 'https://mc.yandex.ru/metrika/tag.js';

type Ym = ((...args: unknown[]) => void) & { a?: unknown[][]; l?: number };

declare global {
  interface Window {
    ym?: Ym;
  }
}

function queue(): Ym {
  if (window.ym) return window.ym;
  const ym: Ym = (...args: unknown[]) => {
    (ym.a ??= []).push(args);
  };
  ym.l = Date.now();
  window.ym = ym;
  return ym;
}

export const yandexMetrika: AnalyticsProvider = {
  name: 'yandexMetrika',

  start(config: AnalyticsConfig) {
    const counter = config.yandexMetrika;
    if (!counter) return null;
    const ym = queue();
    if (!document.querySelector(`script[src="${METRIKA_SCRIPT}"]`)) {
      const script = document.createElement('script');
      script.async = true;
      script.src = METRIKA_SCRIPT;
      document.head.appendChild(script);
    }
    ym(counter.id, 'init', {
      defer: true,
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: counter.webvisor,
    });
    return {
      page: (url, referrer) => window.ym?.(counter.id, 'hit', url, { referer: referrer }),
      goal: (name) => window.ym?.(counter.id, 'reachGoal', name),
    };
  },
};
